import { createPopup, IPopup } from "../components/popup-new";
import { compositeDisposable, IDisposable } from "../utils/disposable";
import {
	expressAddClass,
	expressEventListener,
	expressQuerySelector,
	expressQuerySelectorAll, expressRemoveClass, getParentWithClassName,
	hasClass, hide, htmlToElement, show
} from "../common/html";
import { createCollapsable, ICollapsable, ICreateCollapsableDependencies } from "../components/collapsable";
import { addParameter, getParameter, updateParameter } from "../utils/querystring";
import { createAmount, IAmount } from "../atoms/amount";
import { getIcon, getIconsAsync } from "../services/content-service";
import { getPrice, IProductPriceDetails } from "../services/order-service";
import { initTooltip, IToolTip } from "../components/tooltip";
import { getCookie } from "../utils/cookie";
import { postHtmlRequest } from "../utils/fetch";
import { formatPrice } from "../utils/price";
import { createCarrousel, ICarrousel } from "../molecules/carrousel";
import { createPopupLauncher } from "../components/popup-launcher";
import { getAspectRatioFromPvc } from "../utils/product";
import { ProductEvent, trackShopInShopEvent } from "../common/events";
import { createProductGrid } from "../components/product-grid";

export interface IDesignSelectionPageDialog {
	readonly open: () => void;
	readonly dispose: () => void;
	readonly getId: () => string;
	readonly resetOtherFormatSpecificClass: () => void;
}

interface IPopupContent {
	key: string;
	content: HTMLElement;
	carrousel?: ICarrousel;
	isDefault: boolean;
	productCode: string;
	preDefinedProductName: string;
}

interface IPriceRequest {
	key: string;
	response: IProductPriceDetails;
}

export async function createDesignSelectionPageDialog(
	defaultProductCode: string,
	productCode: string,
	productCategory: string,
	defaultPreDefinedProductName: string,
	preDefinedProductName: string,
	aspectRatio: string,
	designGroup: string,
	packShotUrl: string,
	contentTheme: string,
	pvc: string,
	timeoutBeforeShowingSkeletonScreen: number,
	entrance: number) {
	let popupEl: IPopup;
	const popupContents: IPopupContent[] = [];
	const priceRequests: IPriceRequest[] = [];
	const optionPricePopups: { id: string, popup: IPopup }[] = [];
	const buttonDisabledClass = 'a-button--disabled';

	let buttonEls: HTMLAnchorElement[];
	let sampleButtonEls: HTMLAnchorElement[];
	let amountEls: HTMLElement[];
	let priceEls: HTMLElement[];
	let optionSelect: HTMLSelectElement;
	let iconEls: HTMLElement[];
	let infoIconEls: HTMLElement[];
	let disposable: IDisposable;
	const collapsibleArray: ICollapsable[] = [];
	let amountArray: IAmount[] = [];
	let tooltips: IToolTip[] = [];
	let otherFormatLinkEl: HTMLElement | null;
	let popupId = '';
	let currentProductCode = productCode || defaultProductCode;
	const currentPvc = pvc;
	let currentPredefinedProductName = preDefinedProductName || defaultPreDefinedProductName;
	const dt = defaultProductCode + '|' + defaultPreDefinedProductName;
	let currentAspectRatio = getAspectRatioFromPvc(currentPvc);
	let carrouselEl: HTMLElement;
	let unSelectableDefaultOptionsFormUrl: string[];
	let hasUserSelectedOptions: boolean = false;
	let hasUserSelectedIconOptions: boolean = false;
	let emptyOtherFormatPopupContent: ChildNode;

	const loadingClass = 'skeleton-loading';

	const open = () => {
		collapsibleArray && collapsibleArray.forEach(c => c.close());
		popupEl.openAsync();

		ProductEvent("productSelection", { viewedUniqueSkuWithDesign: popupEl.popup.dataset.uniqueskuwithdesign });
	};

	const resetOtherFormatSpecificClass = () => {
		expressRemoveClass(popupEl.popup, 'p-design-selection-page--different-formats');
	};

	const getColorFromPredefinedProductName = (predefinedProductName: string) => {
		let color = "";
		if (predefinedProductName && predefinedProductName.indexOf("_") > 0) {
			const arr = predefinedProductName.split('_');
			color = arr[arr.length - 1];
		}
		return color;
	};
	const createPopupContentKey = (prefix?: string) => {
		const key = currentProductCode + '_' + getColorFromPredefinedProductName(currentPredefinedProductName) + '_' + getSelectedOptions(false).join(',') + '_' + currentAspectRatio + currentPvc;
		if (!prefix) return key;
		return prefix + '_' + key;
	};

	const getSelectedOptions = (skipUserSelectedCheck = true) => {
		const options: string[] = [];

		if (optionSelect && (skipUserSelectedCheck || hasUserSelectedOptions)) {
			// if options are passed and not in drop down => selected index -1
			const selectedIndex = optionSelect.selectedIndex <= -1 ? 0 : optionSelect.selectedIndex;
			const selectedDropDownOption = optionSelect.options[selectedIndex].value;
			if (selectedDropDownOption) options.push(selectedDropDownOption.toLowerCase());
		}

		if (iconEls && (skipUserSelectedCheck && hasUserSelectedIconOptions)) {
			const selectedIconOption = iconEls.find(el => el.dataset.optioncode && hasClass(el, 'v2-a-icon--selected'));
			if (selectedIconOption && selectedIconOption.dataset.optioncode && !selectedIconOption.dataset.optioncode.toLowerCase().startsWith("nooption")) options.push(selectedIconOption.dataset.optioncode.toLowerCase());
		}

		if (!skipUserSelectedCheck) return options; // don't add unselectable when skipUserSelectedCheck so the defaults are selected

		if (unSelectableDefaultOptionsFormUrl && unSelectableDefaultOptionsFormUrl.length) {
			for (const unselectableOption of unSelectableDefaultOptionsFormUrl)
				options.indexOf(unselectableOption.toLowerCase()) < 0 && options.push(unselectableOption.toLowerCase());
		}

		return options;
	};

	const setSelectedIcon = (iconEl: HTMLElement) => {
		const iconSetEl = getParentWithClassName(iconEl, 'technical-icon-set');
		if (!iconSetEl) return;
		const iconElsInSet = expressQuerySelectorAll<HTMLElement>(iconSetEl, '.technical-icon');
		iconElsInSet.forEach(el => {
			expressRemoveClass(el, 'v2-a-icon--selected');
			expressAddClass(el, 'v2-a-icon--unselected');
		});
		expressRemoveClass(iconEl, 'v2-a-icon--unselected');
		expressAddClass(iconEl, 'v2-a-icon--selected');
	};

	const addSkeleton = (popup: HTMLElement) => {
		const skeletonScreens = expressQuerySelectorAll<HTMLElement>(popup, '.technical-skeleton-container');
		skeletonScreens.forEach(el => expressAddClass(el, loadingClass));
	};
	const removeSkeleton = (popup: HTMLElement) => {
		const skeletonScreens = expressQuerySelectorAll<HTMLElement>(popup, '.technical-skeleton-container');
		skeletonScreens.forEach(el => expressRemoveClass(el, loadingClass));
	};

	const init = async () => {
		const getAndSetPrice = async () => {
			const getUpdatedPrice = async () => {
				const infoTooltipEl = expressQuerySelector<HTMLElement>(infoIconEls[0].parentElement, '.technical-price-tooltip', true);
				const forceQuantityBreak = infoTooltipEl.dataset.quantitypricebreak || '-1';
				const pvc = currentProductCode || '';
				const options = getSelectedOptions();
				const amount = getAmount();
				const promoCode = getCookie("promo");
				let forceQuantityBreakInt: number;
				if (forceQuantityBreak)
					forceQuantityBreakInt = parseInt(forceQuantityBreak, 10);
				const key = pvc + '_' + amount + '_' + options.join(',') + '_' + promoCode + '_' + forceQuantityBreakInt + '';
				const savedPriceRequest = priceRequests.find(el => el.key === key);
				if (savedPriceRequest) {
					return savedPriceRequest.response;
				} else {
					const response = await getPrice(pvc, amount, 0, options, promoCode, forceQuantityBreakInt);
					priceRequests.push({ key, response } as IPriceRequest);
					return response;
				}
			};
			const updatePriceAndInfo = (infoTooltipEl: HTMLElement, priceDetails: IProductPriceDetails) => {
				const templateEl = expressQuerySelectorAll<HTMLElement>(infoTooltipEl, '.technical-price-template')[0];
				const containerEl = expressQuerySelector<HTMLElement>(infoTooltipEl, '.technical-price-container', true);
				containerEl.innerHTML = '';
				if (priceDetails.Price) {
					priceEls.forEach(el => {
						el.innerHTML = formatPrice(priceDetails.Price);
					});
				}
				if (priceDetails.PriceItems) {
					priceDetails.PriceItems.forEach(item => {
						// no item properties to set => skip
						if (!item.Description && !item.CalculationDescription && !item.ItemTotal)
							return;

						const newEl = templateEl.cloneNode(true) as HTMLElement;
						const description = expressQuerySelector<HTMLElement>(newEl, '.technical-price-tooltip-description', true);
						item.Description ? description.innerHTML = item.Description : description.remove();

						const calculation = expressQuerySelector<HTMLElement>(newEl, '.technical-price-tooltip-calculation', true);
						item.CalculationDescription ? calculation.innerHTML = item.CalculationDescription : calculation.remove();

						const result = expressQuerySelector<HTMLElement>(newEl, '.technical-price-tooltip-result', true);
						item.ItemTotal ? result.innerHTML = item.ItemTotal : result.remove();

						containerEl.insertAdjacentElement('beforeend', newEl);
					});
				}
			};

			const priceResult = await getUpdatedPrice();

			for (const infoIconEl of infoIconEls) {
				const infoTooltipEl = expressQuerySelector<HTMLElement>(infoIconEl.parentElement, '.technical-price-tooltip', true);
				updatePriceAndInfo(infoTooltipEl, priceResult);
			}
		};

		const getAmount = () => {
			let amount = 0;
			amountArray.forEach(amountEl => {
				amount += parseInt(amountEl.getCurrentValue() || "1", 10);
			});
			return amount;
		};

		const onButtonClick = (ev: MouseEvent) => {
			ev.preventDefault();
			const el = <HTMLAnchorElement>(ev.currentTarget);

			// already disabled / clicked -> skip
			if (hasClass(el, buttonDisabledClass)) {
				return;
			}

			// disable the button
			expressAddClass(el, buttonDisabledClass);
			setTimeout(() => expressRemoveClass(el, buttonDisabledClass), 10000); // re-enable the button after 10 sec, just in case

			let link = el.href;

			if (amountArray.length === 1) {
				let amount = 0;
				let code = '';
				amountArray.forEach(amountEl => {
					amount += parseInt(amountEl.getCurrentValue() || "1", 10);
					code = amountEl.getCode();
				});
				if (amount > 0)
					if (code)
						link = addParameter('quantity', code + '|' + amount + '', link);
					else
						link = addParameter('quantity', amount + '', link);
			} else {
				let amountString = '';
				amountArray.forEach(amountEl => {
					const code = amountEl.getCode();
					const value = parseInt(amountEl.getCurrentValue() || "0", 10);
					if (code && value > 0)
						amountString += code + '|' + amountEl.getCurrentValue() + ',';
				});
				link = addParameter('quantity', amountString.slice(0, -1), link);
			}

			const options = getSelectedOptions().join(',');
			if (options)
				link = addParameter('options', options, link);

			if (contentTheme && link.indexOf("contentTheme?") < 0)
				link = addParameter('contentTheme', contentTheme, link);

			if (link)
				window.location.href = link;
		};

		let skeletonTimeout;
		const updatePopupContent = async (popup: HTMLElement, clickedIconEl: HTMLElement) => {
			dispose();
			const key = createPopupContentKey();
			const existingPopupContent = popupContents.find(pc => pc.key === key);

			if (existingPopupContent && existingPopupContent.content) {
				currentProductCode = existingPopupContent.productCode;
				currentPredefinedProductName = existingPopupContent.preDefinedProductName;
				popupEl.replaceContent(existingPopupContent.content);
				addHandlers(popupEl.popup, false);
				if (existingPopupContent.carrousel) {
					existingPopupContent.carrousel.reInit();
					existingPopupContent.carrousel.reStart();
				}
				updateQuantity(popupEl.popup);

				const body = expressQuerySelector<HTMLElement>(popupEl.popup, '.technical-popup-body', false);
				body && setUnselectableOptions(body.dataset.defaultoptions?.split(',') ?? []);
				updateUrl(getSelectedOptions().join(','), getParameter('quantity'));

				await getAndSetPrice();
				removeSkeleton(popup);
				return Promise.resolve();
			} else {
				const data = {
					productCode: currentProductCode,
					productCategory,
					preDefinedProductName: currentPredefinedProductName,
					aspectRatio: currentAspectRatio,
					designGroup,
					theme: getParameter('theme') ?? '',
					forWho: getParameter('forwho') ?? '',
					forWhat: getParameter('forwhat') ?? '',
					forWhen: getParameter('forwhen') ?? '',
					options: getSelectedOptions(false),
					pvc: currentPvc,
					selectedQuantity: decodeURIComponent(getParameter('quantity') || ''),
					entrance
				};

				skeletonTimeout = setTimeout(() => addSkeleton(popup), timeoutBeforeShowingSkeletonScreen);
				return postHtmlRequest("/apicore/dialog/designSelectionPageContentDialog", data, { headers: new Headers({ 'Content-type': 'application/json; charset=utf-8' }) }).then(
					res => {
						if (!res) return;
						const node = htmlToElement(res);
						popupEl.replaceContent(node);
						const popupContentEl = expressQuerySelector<HTMLElement>(popup, '.technical-popup-content', true);
						const carrousel = addHandlers(popup, true);
						updateQuantity(popupContentEl);

						const iconEl = expressQuerySelector<HTMLElement>(popupContentEl, '#' + clickedIconEl.id, false);
						if (iconEl) setSelectedIcon(iconEl);

						const body = expressQuerySelector<HTMLElement>(popupContentEl, '.technical-popup-body', false);
						body && setUnselectableOptions(body.dataset.defaultoptions?.split(',') ?? []);

						getAndSetPrice();
						popupContents.push({
							key: createPopupContentKey(),
							content: popupContentEl,
							carrousel,
							isDefault: false,
							productCode: currentProductCode,
							preDefinedProductName: currentPredefinedProductName,
						} as IPopupContent);
					}
				).finally(() => {
					clearTimeout(skeletonTimeout);
					removeSkeleton(popup);
				});
			}
		};

		const addHandlers = (popup: HTMLElement, initMode: boolean, initCarrousel = true) => {
			disposable = compositeDisposable(undefined);
			buttonEls = expressQuerySelectorAll<HTMLAnchorElement>(popup, '.technical-continue-button');
			sampleButtonEls = expressQuerySelectorAll<HTMLAnchorElement>(popup, '.technical-sample-button');
			amountEls = expressQuerySelectorAll<HTMLElement>(popup, '.technical-amount');
			priceEls = expressQuerySelectorAll<HTMLElement>(popup, '.technical-price-tag');
			optionSelect = expressQuerySelector<HTMLSelectElement>(popup, '.technical-option-select', false);
			iconEls = expressQuerySelectorAll<HTMLElement>(popup, '.technical-icon');
			infoIconEls = expressQuerySelectorAll<HTMLElement>(popup, '.technical-price-info-icon');
			carrouselEl = expressQuerySelector<HTMLElement>(popup, '.technical-carrousel', false);
			otherFormatLinkEl = expressQuerySelector<HTMLElement>(popup, '.technical-different-formats-button', false);

			const collapsibleEls = expressQuerySelectorAll<HTMLElement>(popup, '.technical-popup-collapsible');
			if (collapsibleEls && collapsibleEls.length > 0)
				collapsibleEls.forEach(el => {
					const onInit = (containerEl: HTMLElement) => {
						initPopupLaunchers(containerEl);
						initOptionPriceInfoLinks(containerEl);
					};
					const initOptionPriceInfoLinks = (containerEl: HTMLElement) => {
						const moreInfoLinkEls = expressQuerySelectorAll<HTMLElement>(containerEl, '.technical-option-price-more-info-link');
						if (moreInfoLinkEls && moreInfoLinkEls.length > 0) {
							const initOptionPricesMoreInfoLink = (moreInfoLinkEl: HTMLElement) => {
								const init = (popupEl: HTMLElement) => {
									const pvcSelect = expressQuerySelector<HTMLSelectElement>(popupEl, ".technical-price-option-select", false);
									const quantityBreakListItemContainer = expressQuerySelector<HTMLElement>(popupEl, ".p-option-price-details__body-container", true);
									const quantityBreakListItems = expressQuerySelectorAll<HTMLElement>(quantityBreakListItemContainer, ".m-quantity-break-list__item-container");
									const noOptionsDiv = expressQuerySelector<HTMLElement>(popupEl, "#noOptionsDiv", true);
									const onChange = () => pvcSelectChange();
									const pvcSelectChange = (selectedValue?: string) => {
										let visibleItems = 0;
										if (!selectedValue) selectedValue = pvcSelect.value;
										else {
											const options = [...pvcSelect.options];
											const selectedOption = options.find(o => o.value.toLowerCase() === selectedValue.toLowerCase());
											if (selectedOption) pvcSelect.selectedIndex = selectedOption.index;
										}
										quantityBreakListItems.forEach(item => {
											const itemPvc = item.dataset.pvc || '';
											if (itemPvc.toLowerCase() == selectedValue.toLowerCase()) {
												show(item);
												visibleItems++;
											} else {
												hide(item);
											}
										});
										if (visibleItems > 0) {
											hide(noOptionsDiv);
											show(quantityBreakListItemContainer);
										} else {
											show(noOptionsDiv);
											hide(quantityBreakListItemContainer);
										}
									};
									if (quantityBreakListItemContainer && quantityBreakListItems && noOptionsDiv) {
										hide(noOptionsDiv);
										if (!pvcSelect) return;
										if (currentPvc) pvcSelectChange(currentPvc);
										else pvcSelectChange();
										expressEventListener(pvcSelect, 'change', onChange);
									}
								};
								const showOptionPricePopup = () => {

									const contentKey = moreInfoLinkEl.dataset.contentkey || '';

									if (contentKey) {
										let uniqueId = contentKey;
										if (uniqueId.indexOf('_') > 0) uniqueId = uniqueId.split('_')[1];
										if (optionPricePopups.filter(p => p.id === uniqueId).length <= 0) {
											createPopup({
												dialogId: 'optionpricedetails_' + uniqueId,
												view: 'OptionPriceDetailsDialog',
												data: {
													productCategoryOptionContentKey: contentKey,
													pvc: currentPvc
												},
												onBeforeOpen: popupEl => init(popupEl)
											}).then(popup => {
												// TODO: Do we need to save the popup? I think the popup script itself opens the existing one
												optionPricePopups.push({ "id": uniqueId, "popup": popup });
												popup.openAsync();
											});
										} else {
											const popup = optionPricePopups.filter(p => p.id === uniqueId);
											if (popup[0]) popup[0].popup.openAsync();
										}
									}
								};
								const onClick = (e: Event) => {
									e.preventDefault();
									if (hasClass(moreInfoLinkEl, 'a-link--disabled"')) return;
									showOptionPricePopup();
								};
								if (moreInfoLinkEl) expressEventListener(moreInfoLinkEl, 'click', onClick);
							};
							moreInfoLinkEls.forEach(el => initOptionPricesMoreInfoLink(el));
						}
					};
					const initPopupLaunchers = (containerEl: HTMLElement) => {
						// create popup launchers (links from cms with a content dialog parser)
						const popupLauncherEls = expressQuerySelectorAll<HTMLElement>(containerEl, '.technical-popup-launcher');
						popupLauncherEls.forEach(el => createPopupLauncher(el));
					};
					const toggleShowMore = (showMoreEl: HTMLElement, reset?: boolean) => {
						const arrow = expressQuerySelector<HTMLElement>(showMoreEl, '.technical-showmore-arrow', true);
						arrow.style.transform = `rotate(${reset || arrow.style.transform.indexOf('-180') >= 0 ? 0 : -180}deg)`;
						showMoreEl.nextElementSibling.classList.toggle('u-hide', reset ? true : undefined);
					};
					const toggleShowMoreEvent = (ev: Event, reset?: boolean) => toggleShowMore((ev.target as HTMLElement).closest('.technical-delivery-collapsibles'), reset);
					const onBeforeOpen = (containerEl: HTMLElement) => {
						const innerCollapsibleEls = expressQuerySelectorAll<HTMLElement>(containerEl, '.technical-delivery-collapsibles');
						innerCollapsibleEls.forEach(x => {
							toggleShowMore(x, true); // reset dropdowns to closed
							x.addEventListener('click', toggleShowMoreEvent);
						});
						return Promise.resolve();
					};
					const onBeforeClose = (containerEl: HTMLElement) => {
						const innerCollapsibleEls = expressQuerySelectorAll<HTMLElement>(containerEl, '.technical-delivery-collapsibles');
						innerCollapsibleEls.forEach(x => x.removeEventListener('click', toggleShowMoreEvent));
						return Promise.resolve();
					};
					const collapsableDeps = {
						contentSelector: '.m-collapsable__content',
						titleElSelector: '.m-collapsable__title',
						scrollIntoViewAfterOpen: false,
						onBeforeOpen,
						onBeforeClose,
						onInit
					} as ICreateCollapsableDependencies;
					collapsibleArray.push(createCollapsable(el, collapsableDeps, { singleOpen: true }));
				});

			const setSelectedIcon = (iconEl: HTMLElement) => {
				const iconSetEl = getParentWithClassName(iconEl, 'technical-icon-set');
				if (iconSetEl) {
					const iconElsInSet = expressQuerySelectorAll<HTMLElement>(iconSetEl, '.technical-icon');
					iconElsInSet.forEach(el => {
						expressRemoveClass(el, 'v2-a-icon--selected');
						expressAddClass(el, 'v2-a-icon--unselected');
					});
					expressRemoveClass(iconEl, 'v2-a-icon--unselected');
					expressAddClass(iconEl, 'v2-a-icon--selected');
				}
			};

			if (optionSelect) {
				const onChange = () => {
					hasUserSelectedOptions = true;
					getAndSetPrice();
					updateUrl(getSelectedOptions().join(','), getParameter('quantity'));
				};
				disposable.add(expressEventListener(optionSelect, 'change', onChange));
			}

			if (sampleButtonEls && sampleButtonEls.length > 0) {
				const onSampleButtonClick = () => {
					trackShopInShopEvent("Order sample product button clicked", { pvc: currentPvc });
				};
				sampleButtonEls.forEach(el => disposable.add(expressEventListener(el, 'click', onSampleButtonClick)));
			}

			if (buttonEls && buttonEls.length > 0) {
				buttonEls.forEach(el => disposable.add(expressEventListener(el, 'click', onButtonClick)));
			}

			if (amountEls && amountEls.length > 0) {
				amountArray = [];
				const updatePrice = () => {
					const amount = getAmount();
					const totalEl = expressQuerySelector<HTMLElement>(popup, '.technical-total', false);
					if (totalEl) totalEl.innerText = amount + '';
					getAndSetPrice();

					buttonEls.forEach(x => x.classList.toggle(buttonDisabledClass, amount <= 0));
					updateUrl(getSelectedOptions().join(','), getQuantity());
				};
				amountEls.forEach(el => {
					amountArray.push(createAmount(el, {
						onSubtractClick: updatePrice,
						onAddClick: updatePrice,
						onManualInput: updatePrice
					}));
				});
			}

			const giveIconsAnId = () => {
				const iconSetEls = expressQuerySelectorAll(popup, '.technical-icon-set');
				iconSetEls.forEach(el => {
					const iconEls = expressQuerySelectorAll<HTMLElement>(el, '.technical-icon');
					iconEls.forEach(iconEl => {
						if (hasClass(iconEl, 'technical-icon-alternative-option')) {
							iconEl.id = "icon-option-" + iconEl.dataset.optioncode;
						}
						if (hasClass(iconEl, 'technical-icon-alternative-product')) {
							iconEl.id = "icon-product-" + iconEl.dataset.productcode;
							if (iconEl.dataset.aspectration)
								iconEl.id += '-' + iconEl.dataset.aspectration;
						}
						if (hasClass(iconEl, 'technical-icon-alternative-color')) {
							iconEl.id = "icon-color-" + getColorFromPredefinedProductName(iconEl.dataset.predefinedproductname);
						}
					});
				});
			};

			if (iconEls && iconEls.length > 0) {
				giveIconsAnId();
				if (initMode) {
					const iconContentKeyDataAttribute = "data-iconcontentkey";
					const iconsWithoutSvg = iconEls.filter(el => !expressQuerySelector(el, 'svg', false));
					const iconKeys = iconsWithoutSvg.map(el => el.getAttribute(iconContentKeyDataAttribute) || "");
					getIconsAsync(iconKeys.filter((x, i, a) => a.indexOf(x) == i)).then(_ => {
						iconsWithoutSvg.forEach(el => {
							if (el.hasAttribute(iconContentKeyDataAttribute)) {
								const iconContentKey = el.getAttribute(iconContentKeyDataAttribute);
								if (iconContentKey) {
									const icon = getIcon(iconContentKey.toLocaleLowerCase().replace("icon.", ""));
									if (icon) {
										fetch(icon)
											.then(response => response.text())
											.then(svg => {
												const iEl = expressQuerySelector<HTMLElement>(el, '.technical-icon-file', true);
												if (!iEl.innerText) iEl.insertAdjacentHTML("afterbegin", svg);
											});
									}
								}
							}
						});
					});
				}
				const onIconCLick = (_: MouseEvent, iconEl: HTMLElement) => {
					// alternative option
					if (hasClass(iconEl, 'technical-icon-alternative-option')) {
						hasUserSelectedIconOptions = true;
						setSelectedIcon(iconEl);
						getAndSetPrice();
						updateUrl(getSelectedOptions().join(','), getQuantity());
					}
					// Alternative product
					if (hasClass(iconEl, 'technical-icon-alternative-product')) {
						currentProductCode = iconEl.dataset.productcode;
						currentPredefinedProductName = iconEl.dataset.predefinedproductname;
						currentAspectRatio = iconEl.dataset.aspectratio || getAspectRatioFromPvc(currentPvc);
						updatePopupContent(popup, iconEl);
						updateUrl(getParameter('options'), getQuantity());
					}
					// Alternative color
					if (hasClass(iconEl, 'technical-icon-alternative-color')) {
						currentPredefinedProductName = iconEl.dataset.predefinedproductname;
						updatePopupContent(popup, iconEl);
						updateUrl(getParameter('options'), getQuantity());
					}
				};
				iconEls.forEach(el => disposable.add(expressEventListener(el, 'click', (e) => onIconCLick(e, el))));
			}

			if (infoIconEls && infoIconEls.length > 0) {
				tooltips = [];
				infoIconEls.forEach(infoIconEl => {
					const infoTooltipEl = expressQuerySelector<HTMLElement>(infoIconEl.parentElement, '.technical-price-tooltip', true);
					tooltips.push(initTooltip({
						buttonEl: infoIconEl,
						containerEl: infoTooltipEl,
						openClass: "is-tooltip-open",
						onOpen: getAndSetPrice
					}));
				});
			}

			if (otherFormatLinkEl) {
				// disable the other formats button => we'll enable it later
				expressAddClass(otherFormatLinkEl, 'a-button--disabled');

				const data = {
					productCode: currentProductCode,
					preDefinedProductName: currentPredefinedProductName,
					productCategoryGroup: getParameter('pcg') ?? '',
					color: '',
					aspectRatio: currentAspectRatio,
					designGroup,
					theme: getParameter('theme') ?? '',
					promoCode: getCookie("promo") ?? '',
					designVariantId: otherFormatLinkEl.dataset.designvariantid
				};

				postHtmlRequest("/apicore/dialog/emptyDesignSelectionPageOtherFormatsContentDialog", data, { headers: new Headers({ 'Content-type': 'application/json; charset=utf-8' }) }).then(
					res => {
						if (!res) return;
						emptyOtherFormatPopupContent = htmlToElement(res);
						expressRemoveClass(otherFormatLinkEl, 'a-button--disabled');
					}
				);

				const otherFormatLinkClick = (e: Event) => {
					e.preventDefault();
					if (hasClass(otherFormatLinkEl, 'a-button--disabled')) return;

					const existingPopupContent = popupContents.find(pc => pc.key === createPopupContentKey('otherFormats'));
					const addFormatPopupHandlers = (formatPopup: HTMLElement) => {
						const backIconEl = expressQuerySelector<HTMLElement>(formatPopup, '.technical-back-icon', false);
						if (backIconEl) {
							const onBackIconClick = () => {
								const currentPopupKey = createPopupContentKey();
								const existingPopupContent = popupContents.find(pc => pc.key === currentPopupKey);
								if (existingPopupContent) {
									addSkeleton(existingPopupContent.content);
									popupEl.replaceContent(existingPopupContent.content);
									expressRemoveClass(popupEl.popup, 'p-design-selection-page--different-formats');
									setTimeout(removeSkeleton, 200, popup);
								}
							};
							disposable.add(expressEventListener(backIconEl, 'click', onBackIconClick));
						}
						const gridEl = expressQuerySelector<HTMLElement>(formatPopup, '.technical-product-grid', false);
						createProductGrid(gridEl, {});
						const anchorEls = expressQuerySelectorAll<HTMLAnchorElement>(gridEl, 'a');
						anchorEls.forEach(anchorEl => {
							disposable.add(expressEventListener(anchorEl, 'click', onButtonClick));
						});
					};
					if (existingPopupContent) {
						popupEl.replaceContent(existingPopupContent.content);
						expressAddClass(popupEl.popup, 'p-design-selection-page--different-formats');
						removeSkeleton(popup);
						addFormatPopupHandlers(existingPopupContent.content);
					} else {
						popupEl.replaceContent(emptyOtherFormatPopupContent);
						expressAddClass(popupEl.popup, 'p-design-selection-page--different-formats');
						postHtmlRequest("/apicore/dialog/designSelectionPageOtherFormatsContentDialog", data, { headers: new Headers({ 'Content-type': 'application/json; charset=utf-8' }) }).then(
							res => {
								if (!res) return;
								const node = htmlToElement(res);
								popupEl.replaceContent(node);
								addFormatPopupHandlers(popupEl.popup);
								const popupContentEl = expressQuerySelector<HTMLElement>(popupEl.popup, '.technical-popup-content', true);
								popupContents.push({
									key: createPopupContentKey('otherFormats'),
									content: popupContentEl,
									carrousel: null,
									isDefault: false
								} as IPopupContent);
							}
						);
					}
				};
				disposable.add(expressEventListener(otherFormatLinkEl, 'click', (e) => otherFormatLinkClick(e)));
			}

			if (carrouselEl && initMode && initCarrousel)
				return createCarrousel(carrouselEl);
			return null;
		};

		const getQuantity = () => {
			if (amountArray.length === 1) {
				let amount = 0;
				let code = '';
				amountArray.forEach(amountEl => {
					amount += parseInt(amountEl.getCurrentValue() || "1", 10);
					code = amountEl.getCode();
				});
				if (amount > 0)
					if (code)
						return `${code}|${amount}`;
				return `${amount}`;
			} else {
				let amountString = '';
				amountArray.forEach(amountEl => {
					const code = amountEl.getCode();
					const value = parseInt(amountEl.getCurrentValue() || "0", 10);
					if (code && value > 0)
						amountString += `${code}|${amountEl.getCurrentValue()},`;
				});
				return amountString.slice(0, -1);
			}
		};

		const updateUrl = (options = '', quantity = '', updatePopupCode = true) => {
			let url = window.location.href;
			url = updateParameter('dt', (options || quantity) && updatePopupCode ? dt : '', url);
			url = updateParameter('pc', (options || quantity) && updatePopupCode ? currentProductCode : '', url);
			url = updateParameter('ppn', (options || quantity) && updatePopupCode ? currentPredefinedProductName : '', url);
			url = updateParameter('options', options, url);
			url = updateParameter('quantity', quantity, url);
			window.history.replaceState(null, null, url);
		};

		const onPopupOpen = (popup: HTMLElement) => {
			const existingPopupContent = popupContents.find(pc => pc.isDefault);

			if (existingPopupContent && existingPopupContent.content) {
				currentProductCode = existingPopupContent.productCode;
				currentPredefinedProductName = existingPopupContent.preDefinedProductName;
				if (existingPopupContent.carrousel) {
					existingPopupContent.carrousel.reInit();
					existingPopupContent.carrousel.reStart();
				}
			} else {
				popupContents.push({
					key: createPopupContentKey(),
					content: expressQuerySelector<HTMLElement>(popup, '.technical-popup-content', true),
					carrousel: createCarrousel(carrouselEl),
					isDefault: true,
					productCode: currentProductCode,
					preDefinedProductName: currentPredefinedProductName,
				} as IPopupContent);
			}

			clearTimeout(skeletonTimeout);
			removeSkeleton(popup);
		};

		const updateQuantity = (popup: HTMLElement) => {
			// set amount
			const quantity = getParameter('quantity')?.split(',') ?? [];
			quantity.forEach(x => {
				const codeParts = x.split('|');
				const code = codeParts.length > 1 ? codeParts[0] : '';
				const value = codeParts.length > 1 ? codeParts[1] : codeParts[0];

				const amount = amountArray.find(x => x.getCode().toLowerCase() === code.toLowerCase());
				if (!amount) return;

				const intValue = value ? parseInt(value, 10) : amount.minAmount;
				if (isNaN(intValue)) return;
				amount.setValue(intValue);
			});

			// update total
			const amount = amountArray.map(x => +x.getCurrentValue()).reduce((a, b) => a + b);
			const totalEl = expressQuerySelector<HTMLElement>(popup, '.technical-total', false);
			if (totalEl) totalEl.innerText = `${amount}`;
		};

		const setUnselectableOptions = (defaultOptions: string[]) => {
			const optionSelectOptions = [];
			if (optionSelect)
				for (let i = 0; i < optionSelect.options.length; i++) {
					optionSelectOptions.push(optionSelect.options[i]);
				}

			// set unSelectableDefaultOptionsFormUrl default options that cant be selected in the popup
			const altOptions = (iconEls && iconEls.length && iconEls.filter(x => x.classList.contains('technical-icon-alternative-option')).map(x => x.dataset.optioncode)) || [];
			unSelectableDefaultOptionsFormUrl = defaultOptions.filter(x => !optionSelectOptions.some(option => option.value === x) && !altOptions.some(opt => opt === x.toLowerCase()));
		};

		const onPopupBeforeOpen = async (popup: HTMLElement) => {
			if (!popup) return;
			skeletonTimeout = setTimeout(() => addSkeleton(popup), timeoutBeforeShowingSkeletonScreen);

			const existingPopupContent = popupContents.find(pc => pc.isDefault);

			if (existingPopupContent && existingPopupContent.content) {
				popupEl.replaceContent(existingPopupContent.content);
				currentProductCode = existingPopupContent.productCode;
				addHandlers(popupEl.popup, false, false);
			} else {
				addHandlers(popupEl.popup, true, false);
			}

			// update new content with new data
			const queryOptions = getParameter('options');
			const options = queryOptions ? decodeURIComponent(queryOptions).split(',') : [];
			const optionSelectOptions = [];
			if (optionSelect)
				for (let i = 0; i < optionSelect.options.length; i++) {
					optionSelectOptions.push(optionSelect.options[i]);
				}

			// icons
			const iconOption = options && iconEls.find(x => x.classList.contains('technical-icon-alternative-option') && options.find(opt => opt === x.id));
			iconOption && setSelectedIcon(iconOption);
			hasUserSelectedIconOptions = false;

			// options
			if (options && options.find(x => optionSelectOptions.some(option => option.value === x))) optionSelect.value = options.find(x => optionSelectOptions.some(option => option.value === x));
			hasUserSelectedOptions = false;

			setUnselectableOptions(options);
			updateQuantity(popup);
			await getAndSetPrice();
		};

		const dispose = () => {
			collapsibleArray.forEach(el => el.dispose());
			amountArray.forEach(el => el.dispose());
			disposable && disposable.dispose();
			tooltips.forEach(el => el.dispose());
			popupContents.forEach(c => c.carrousel && c.carrousel.dispose());
		};

		const onPopupClose = () => {
			updateUrl('', undefined, false); // clear current open popup url
			dispose();
			hasUserSelectedIconOptions = false;
			hasUserSelectedOptions = false;
		};

		popupId = 'DesignSelectionPageDialog-' + currentProductCode + '_' + productCategory + '-' + currentPredefinedProductName + '-' + designGroup + '-' + aspectRatio + '-' + currentPvc;
		popupEl = await createPopup({
			dialogId: popupId.replace("~", "").replace(/\./g, ''),
			view: 'DesignSelectionPageDialog',
			data: {
				productCode: currentProductCode,
				productCategory,
				preDefinedProductName: currentPredefinedProductName,
				aspectRatio,
				theme: getParameter('theme') ?? '',
				forWho: getParameter('forwho') ?? '',
				forWhat: getParameter('forwhat') ?? '',
				forWhen: getParameter('forwhen') ?? '',
				designGroup,
				packShotUrl,
				contentTheme,
				pvc: currentPvc,
				selectedQuantity: decodeURIComponent(getParameter('quantity') || ''),
				options: decodeURIComponent(getParameter('options') || ''),
				crossSellFrom: getParameter('crossSellFrom') ?? '',
				entrance
			},
			forceRefresh: false,
			onBeforeOpen: onPopupBeforeOpen,
			onOpen: onPopupOpen,
			onClose: onPopupClose
		});
	};

	const getId = () => {
		return popupId;
	};

	await init();

	return {
		open,
		dispose: () => disposable.dispose,
		getId,
		resetOtherFormatSpecificClass
	} as IDesignSelectionPageDialog;
}
